import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CreditCard, ChevronDown, ChevronUp, Check, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export default function CardSelectionSection({ selectedCardholder, selectedCard, onCardSelect, disabled }) {
  const [expanded, setExpanded] = useState(true); // Start expanded by default

  const cards = [
    { id: "2445", name: "2445 Virtual", status: "Active", type: "Virtual" },
    { id: "3890", name: "3890 Physical", status: "Active", type: "Physical" },
    { id: "1993", name: "1993 Virtual", status: "Inactive", type: "Virtual" }
  ];

  const handleCardSelect = (card) => {
    onCardSelect(card);
    setExpanded(false);
  };

  const clearSelectedCard = () => {
    onCardSelect(null);
    setExpanded(true);
  };

  return (
    <Card className={`mb-6 border-l-4 border-l-orange-500 shadow-md ${disabled ? 'opacity-60' : ''}`}>
      <CardHeader className="flex flex-row items-center justify-between py-4">
        <CardTitle 
          className={`text-lg font-semibold flex items-center ${!disabled ? 'cursor-pointer' : ''}`}
          onClick={() => !disabled && setExpanded(!expanded)}
        >
          <CreditCard className="mr-2 h-5 w-5 text-orange-500" />
          Card Selection
          {expanded ? 
            <ChevronUp className="ml-2 h-4 w-4" /> : 
            <ChevronDown className="ml-2 h-4 w-4" />
          }
        </CardTitle>
        {selectedCard && (
          <div className="flex items-center">
            <span className="text-sm bg-green-100 text-green-800 py-1 px-3 rounded-full flex items-center">
              <Check className="mr-1 h-3 w-3" />
              Card Selected
            </span>
          </div>
        )}
      </CardHeader>

      <AnimatePresence>
        {expanded && !selectedCard && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <CardContent className="pb-6">
              {!disabled ? (
                <div className="grid gap-3">
                  {cards.map((card) => (
                    <div
                      key={card.id}
                      className={`p-4 border rounded-lg 
                        ${card.status === "Inactive" ? 
                          "opacity-60 cursor-not-allowed" : 
                          "cursor-pointer hover:border-orange-200 hover:bg-orange-50"}`}
                      onClick={() => card.status === "Active" && handleCardSelect(card)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <CreditCard className="h-5 w-5 text-gray-500" />
                          <div>
                            <div className="font-medium">Card ending in {card.id}</div>
                            <div className="text-sm text-gray-500">{card.type} Card</div>
                          </div>
                        </div>
                        <Badge 
                          variant="outline" 
                          className={card.status === "Active" 
                            ? "bg-green-100 text-green-800 border-green-200" 
                            : "bg-red-100 text-red-800 border-red-200"}
                        >
                          {card.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center p-4 text-gray-500">
                  Please select a cardholder first
                </div>
              )}
            </CardContent>
          </motion.div>
        )}

        {expanded && selectedCard && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <CardContent className="py-2">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <CreditCard className="h-5 w-5 text-gray-500" />
                  <div>
                    <div className="font-medium">Card ending in {selectedCard.id}</div>
                    <div className="text-sm text-gray-500">{selectedCard.type} Card</div>
                  </div>
                  <Badge 
                    variant="outline" 
                    className="bg-green-100 text-green-800 border-green-200"
                  >
                    {selectedCard.status}
                  </Badge>
                </div>
                {!disabled && (
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={clearSelectedCard}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                )}
              </div>
            </CardContent>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}